import React, { useEffect, useState } from "react";
import { Loader2, Home, MapPin, AlertTriangle, Check, RefreshCw } from "lucide-react";
import api, { apiErrorMessage } from "../services/api";
import { toast } from "sonner";

const fmt = (n) => (n ?? 0).toFixed(2).replace(".", ",");
const fmtDate = (d) => { const t = new Date(d); return Number.isNaN(t.getTime()) ? d : t.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" }); };

const Offer = ({ o, selected, free, onPick, disabled }) => (
  <button type="button" data-testid={`ship-offer-${o.code}`} onClick={() => onPick(o)} disabled={disabled} className={`w-full text-left rounded-xl border px-3 py-2.5 flex items-center gap-3 transition-colors ${selected ? "border-[#FF3B3B] bg-[#1A1114]" : "border-[#222735] bg-[#10131A] hover:border-slate-500"}`}>
    {o.logo ? <img src={o.logo} alt={o.operator_label} className="w-9 h-9 object-contain rounded bg-white p-0.5" /> : <div className="w-9 h-9 rounded bg-[#181C26]" />}
    <div className="flex-1 min-w-0">
      <div className="text-[13px] font-semibold text-slate-50 leading-snug">{o.operator_label} <span className="text-slate-400 font-normal">· {o.service_label}</span></div>
      <div className="text-[11px] text-slate-500 leading-snug">{o.delivery_label}</div>
      {o.delivery_date && <div className="text-[11px] text-slate-400 leading-snug" data-testid={`ship-offer-eta-${o.code}`}>Livraison estimée : {fmtDate(o.delivery_date)}</div>}
    </div>
    <div className="text-right shrink-0"><div className={`text-[14px] font-bold ${free ? "text-emerald-400" : "text-slate-50"}`}>{free ? "Offerte" : `${fmt(o.price_ttc)} €`}</div>{free && <div className="text-[10px] text-slate-500 line-through">{fmt(o.price_ttc)} €</div>}</div>
    {selected && <Check size={16} className="text-[#FF3B3B] shrink-0" />}
  </button>
);

const PointPicker = ({ offer, chosen, onChoose }) => {
  const [pts, setPts] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setPts(null); setErr("");
    api.get("/cart/parcel-points", { params: { offer_code: offer.code } }).then(({ data }) => setPts(data.points || [])).catch((e) => { setErr(apiErrorMessage(e, "Points relais indisponibles")); setPts([]); });
  }, [offer.code]);
  if (pts === null) return <div className="flex items-center gap-2 text-[12px] text-slate-400 py-2"><Loader2 size={14} className="animate-spin" /> Recherche des points relais…</div>;
  if (err) return <div className="text-[12px] text-amber-300 py-2" data-testid="points-error"><AlertTriangle size={13} className="inline mr-1" />{err}</div>;
  if (!pts.length) return <div className="text-[12px] text-amber-300 py-2">Aucun point relais trouvé près de votre adresse.</div>;
  return (
    <div className="space-y-1.5 max-h-64 overflow-y-auto thin-scroll pr-1" data-testid="points-list">
      {pts.map((p) => (
        <button type="button" key={p.code} data-testid={`point-${p.code}`} onClick={() => onChoose(p)} className={`w-full text-left rounded-lg border px-3 py-2 text-[12px] ${chosen?.code === p.code ? "border-[#FF3B3B] bg-[#1A1114]" : "border-[#222735] bg-[#0D0F14] hover:border-slate-500"}`}>
          <div className="font-semibold text-slate-50 flex items-center justify-between"><span className="truncate">{p.name}</span>{p.distance_m != null && <span className="text-slate-500 font-normal shrink-0 ml-2">{p.distance_m >= 1000 ? `${(p.distance_m / 1000).toFixed(1)} km` : `${p.distance_m} m`}</span>}</div>
          <div className="text-slate-400">{p.street}, {p.postal_code} {p.city}</div>
        </button>
      ))}
    </div>
  );
};

export const ShippingChoice = ({ items, channel = "perso", onChange }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("home");
  const [pendingRelay, setPendingRelay] = useState(null);
  const [saving, setSaving] = useState(false);
  const key = items.map((i) => `${i.id}:${i.qty}`).join("|");

  const load = () => api.get("/cart/shipping-offers", { params: { channel } }).then(({ data: d }) => {
    setData(d);
    if (d.selected?.offer?.is_relay) setTab("relay");
    else if (d.offers_home?.length === 0 && d.offers_relay?.length > 0) setTab("relay");
    onChange && onChange(d.selected || null, d);
  }).catch(() => setData({ status: "error", message: "Impossible de charger les offres de livraison." }));
  useEffect(() => { if (items.length) load(); }, [key, channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = async (offer, point) => {
    setSaving(true);
    try {
      await api.post("/cart/shipping-choice", { offer_code: offer.code, quote_id: data.quote_id, pickup_point: point || null, channel });
      setPendingRelay(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Choix impossible")); } finally { setSaving(false); }
  };

  if (!items.length || !data || data.status === "disabled" || data.status === "empty") return null;
  const sel = data.selected;
  return (
    <div className="rounded-2xl border border-[#1E232D] bg-[#0D0F14] p-4 space-y-3" data-testid="shipping-choice">
      <div className="flex items-center justify-between"><div className="font-bold text-slate-50 text-[15px]">Mode de livraison</div>
        <button type="button" data-testid="shipping-reload" onClick={load} className="text-slate-500 hover:text-slate-200"><RefreshCw size={14} /></button></div>
      {data.status !== "ok" && (
        <div className="rounded-lg border border-amber-800/60 bg-amber-950/30 px-3 py-2 text-[13px] text-amber-200 flex items-start gap-2" data-testid={`shipping-status-${data.status}`}>
          <AlertTriangle size={15} className="shrink-0 mt-0.5" /><span>{data.message || "Frais de livraison indisponibles pour le moment."}{data.status === "missing_weight" && data.unknown_items?.length ? ` (${data.unknown_items.join(", ")})` : ""}</span>
        </div>
      )}
      {data.status === "ok" && (
        <>
          <div className="flex gap-1 border-b border-[#1E232D]">
            {[["home", "À domicile", Home, data.offers_home.length], ["relay", "En point relais", MapPin, data.offers_relay.length]].map(([k, l, Icon, n]) => (
              <button type="button" key={k} data-testid={`ship-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-1.5 text-[13px] font-semibold px-3 py-2 -mb-px border-b-2 ${tab === k ? "border-[#FF3B3B] text-slate-50" : "border-transparent text-slate-400 hover:text-slate-200"}`}><Icon size={14} /> {l} <span className="text-slate-500 font-normal">({n})</span></button>
            ))}
          </div>
          <div className="space-y-2">
            {(tab === "home" ? data.offers_home : data.offers_relay).length === 0 && <div className="text-[12px] text-slate-500 py-2">Aucune offre {tab === "home" ? "à domicile" : "en point relais"} pour cette destination.</div>}
            {(tab === "home" ? data.offers_home : data.offers_relay).map((o) => (
              <div key={o.code}>
                <Offer o={o} free={data.free_shipping} disabled={saving} selected={sel?.offer_code === o.code || pendingRelay?.code === o.code} onPick={(off) => (off.is_relay ? setPendingRelay(off) : choose(off))} />
                {o.is_relay && (pendingRelay?.code === o.code || (sel?.offer_code === o.code && !sel.pickup_point)) && (
                  <div className="mt-2 ml-2 pl-3 border-l-2 border-[#222735]"><div className="text-[12px] text-slate-400 mb-1">Choisissez votre point relais :</div><PointPicker offer={o} chosen={sel?.pickup_point} onChoose={(p) => choose(o, p)} /></div>
                )}
                {o.is_relay && sel?.offer_code === o.code && sel.pickup_point && pendingRelay?.code !== o.code && (
                  <div className="mt-1 ml-2 pl-3 border-l-2 border-[#FF3B3B] text-[12px] text-slate-300" data-testid="chosen-point">Point relais : <strong>{sel.pickup_point.name}</strong>, {sel.pickup_point.street}, {sel.pickup_point.postal_code} {sel.pickup_point.city} <button type="button" onClick={() => setPendingRelay(o)} className="text-[#FF3B3B] ml-1 underline">changer</button></div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
